import groupBy from 'lodash/groupBy';
import { createSelector } from 'react-sweet-state';
import type { BoardState, Issue } from '../types';
import { isGroupByFieldAllowedByKey, isGroupByFieldAllowedByType } from '../utils';

export interface TransitionsByStatusProps {
	fromStatusId: number;
	toStatusId: number;
}

export const getTransitionsByStatus = createSelector(
	(state: BoardState) => state.transitions,
	(__, props: TransitionsByStatusProps) => props.fromStatusId,
	(__, props: TransitionsByStatusProps) => props.toStatusId,
	(transitions, fromStatusId, toStatusId) =>
		transitions.filter((transition) => {
			if (transition.isInitial) {
				return false;
			}
			if (
				fromStatusId !== toStatusId &&
				transition.isGlobal &&
				toStatusId === transition.toStatusId
			) {
				return true;
			}
			return (
				toStatusId === Number(transition.toStatusId) &&
				fromStatusId === Number(transition.fromStatusId)
			);
		}),
);

export const getTransitionById = createSelector(
	(state: BoardState) => state.transitions,
	(__, props: { transitionId: number | null }) => props.transitionId,
	(transitions, transitionId) =>
		transitions.find((transition) => transition.transitionId === transitionId),
);

export const getColumnByStatusId = createSelector(
	(state: BoardState) => state.columns,
	(__, props: { statusId: number }) => props.statusId,
	(columns, statusId) => columns.find((column) => column.statusId === statusId),
);

export const getInitialTransition = createSelector(
	(state: BoardState) => state.transitions,
	(transitions) => transitions.find((transition) => transition.isInitial),
);

export const getIssueById = (state: Readonly<BoardState>): ((id: number) => Issue) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const idIssueMap = state.issues.reduce<Record<string, any>>(
		(issuesMap, issue) =>
			Object.assign(issuesMap, {
				[issue.id]: issue,
			}),
		{},
	);
	return (id: number) => idIssueMap[id];
};

// This selector drives whether there's only one possible transition to any given status and used to show/hide dropzones
export const hasMultipleTransitions = createSelector(
	(state: BoardState) => state.transitions,
	(transitions) => {
		// Always show dropzones for self transitions since dragging in the same column will rank without the transitions
		if (transitions.some((transition) => transition.fromStatusId === transition.toStatusId)) {
			return true;
		}
		// Group transitions by target status filtering out initial ones since they can't be executed implicitly
		const transitionsGroupedByToStatus = groupBy(
			transitions.filter((transition) => !transition.isInitial),
			(transition) => transition.toStatusId,
		);
		return Object.keys(transitionsGroupedByToStatus)
			.map((toStatusId) => transitionsGroupedByToStatus[toStatusId])
			.some(
				(transitionsByTargetStatus) =>
					transitionsByTargetStatus.length > 1 &&
					(transitionsByTargetStatus.some((transition) => transition.isGlobal) ||
						new Set(transitionsByTargetStatus.map((transition) => transition.fromStatusId)).size <
							transitionsByTargetStatus.length),
			);
	},
);

export const getActiveWorkflowDetails = createSelector(
	(state: BoardState) => state.workflowsDetails,
	(state) => state.workflowId,
	(workflowsDetails, workflowId) => {
		if (workflowId == null) {
			return undefined;
		}
		return (
			workflowsDetails.find((workflowsDetail) => workflowsDetail.workflowId === workflowId) ??
			workflowsDetails[0]
		);
	},
);

export const hasMultipleWorkflows = createSelector(
	(state: BoardState) => state.workflowsDetails,
	(workflowsDetails) => workflowsDetails?.length > 1,
);

export const getWorkflowDetailsWithoutSubtasksOnlyWorklows = createSelector(
	(state: BoardState) => state.workflowsDetails,
	(workflowsDetails) =>
		workflowsDetails.filter((workflowDetails) => {
			if (workflowDetails.issueTypes == null || workflowDetails.issueTypes.length === 0) {
				return false;
			}
			return workflowDetails.issueTypes.some((issueType) => issueType.hierarchyLevel >= 0);
		}),
);

export const isGroupByFieldAllowed = createSelector(
	(state: BoardState) => state.issueFields,
	(state) => state.issueTypes,
	(_, props: { fieldType?: string; fieldKey?: string }) => props.fieldType,
	(_, props) => props.fieldKey,
	(issueFields, issueTypesMap, fieldType, fieldKey) => {
		if (fieldType != null) {
			return isGroupByFieldAllowedByType(fieldType, issueFields, issueTypesMap);
		}

		if (fieldKey != null) {
			return isGroupByFieldAllowedByKey(fieldKey, issueFields, issueTypesMap);
		}
		return true;
	},
);
